import { aperture } from "../design-system/components/aperture.ts";
import { button } from "../design-system/components/button.ts";
import { icon } from "../design-system/components/icon.ts";
import { el } from "../design-system/dom.ts";
import type { ShellState } from "./state.ts";

/**
 * Application shell — skip link, app bar, demonstration banner, main, footer.
 *
 * ## Landmarks
 *
 * `<header>`, `<main>` and `<footer>` are used as themselves so a screen-reader
 * user gets real landmark navigation. `<main>` carries the id the skip link
 * targets, and the router moves focus into it on every navigation.
 *
 * ## The demonstration banner is not decoration
 *
 * This build renders a complete-looking transfer flow whose authorization step
 * does not exist and whose claim mechanics are still gated on Phase 02 PV-16. A
 * polished interface that stays silent about that is actively misleading — to a
 * reviewer, to a bank, and to anyone the screen is shown to. The banner is
 * therefore a permanent part of the shell rather than a removable flag, and it
 * is designed to look deliberate instead of looking like a debug warning.
 */

export interface Shell {
  readonly root: HTMLElement;
  readonly outlet: HTMLElement;
}

function themeToggle(state: ShellState): HTMLElement {
  const node = button({
    label: "",
    variant: "ghost",
    size: "sm",
    onClick: () => {
      state.setTheme(state.resolvedTheme === "dark" ? "light" : "dark");
    },
  });

  function sync(): void {
    const dark = state.resolvedTheme === "dark";
    // An icon-only control still needs a name, and the name has to say what
    // pressing it does, not what the current state is.
    node.setAttribute("aria-label", dark ? "Switch to light theme" : "Switch to dark theme");
    node.replaceChildren(el("span", { staticHtml: icon(dark ? "sun" : "moon", 18) }));
  }

  sync();
  state.subscribe(sync);
  return node;
}

/**
 * The identity lockup in the app bar.
 *
 * Which identity leads is a commercial setting, not a layout one, so there is
 * one lockup and it reads the tenant's attribution mode:
 *
 * - `nura` — Nura's wordmark leads, with the bank's chip beside it when a bank
 *   is selected. The reference build.
 * - anything else — the bank leads and Nura is not in the bar at all.
 *
 * There is deliberately no second component for the co-branded case. Two
 * lockups would drift apart the first time either changed.
 */
function brandLockup(state: ShellState): HTMLElement {
  const root = el("div", { class: "nura-app-bar__lead" });

  function nuraMark(): HTMLElement {
    return el("a", { class: "nura-wordmark", attrs: { href: "/", "aria-label": "Nura — home" } }, [
      el("span", { class: "nura-wordmark__type", text: "nura" }),
      el("span", { class: "nura-wordmark__mark", staticHtml: aperture(14, { weight: 10 }) }),
    ]);
  }

  function bankChip(): HTMLElement {
    return el("span", { class: "nura-tenant-chip" }, [
      el("span", { class: "nura-tenant-chip__mark", text: state.tenant.initials }),
      el("span", { class: "nura-tenant-chip__name", text: state.tenant.name }),
      // The chip reads as a bare bank name otherwise; the relationship it
      // encodes is the point, so it is spelled out for assistive technology.
      el("span", { class: "nura-sr-only", text: " — the bank providing this transfer" }),
    ]);
  }

  function bankLockup(): HTMLElement {
    return el("span", { class: "nura-bank-lockup" }, [
      el("span", { class: "nura-bank-lockup__mark", text: state.tenant.initials }),
      el("span", { class: "nura-bank-lockup__name", text: state.tenant.name }),
    ]);
  }

  function sync(): void {
    const tenant = state.tenant;

    if (tenant.attribution === "nura") {
      // Nura leads. A "Nura" chip beside the Nura wordmark would be noise, so
      // the reference identity shows no chip.
      root.replaceChildren(nuraMark());
      if (tenant.isReference !== true) root.appendChild(bankChip());
      return;
    }

    // The bank leads and Nura is absent from the bar, in both the co-branded
    // and the fully white-labelled case.
    root.replaceChildren(bankLockup());
  }

  sync();
  state.subscribe(sync);
  return root;
}

/**
 * The footer line, which is where attribution lives when Nura does not lead.
 *
 * `powered-by-nura` renders a quiet mark-plus-wording lockup; `none` renders
 * the institution's own name and no Nura wording at all.
 */
function attributionLine(state: ShellState): HTMLElement {
  const line = el("p", { class: "nura-attribution" });

  function sync(): void {
    switch (state.tenant.attribution) {
      case "nura":
        line.replaceChildren(el("span", { text: "Nura — bank-branded transfer orchestration." }));
        break;
      case "powered-by-nura":
        line.replaceChildren(
          el("span", {
            class: "nura-attribution__mark",
            staticHtml: aperture(13, { weight: 11 }),
          }),
          el("span", { text: "Powered by Nura" }),
        );
        break;
      default:
        line.replaceChildren(el("span", { text: state.tenant.name }));
        break;
    }
  }

  sync();
  state.subscribe(sync);
  return line;
}

export function createShell(state: ShellState): Shell {
  const outlet = el("main", {
    class: "nura-main",
    attrs: { id: "nura-main", tabindex: "-1" },
  });

  const appBar = el("header", { class: "nura-app-bar" }, [
    brandLockup(state),
    el("div", { class: "nura-app-bar__trail" }, [themeToggle(state)]),
  ]);

  // A labelled `<aside>` rather than a bare `<div>`: standing commentary about
  // the whole application is complementary content, and content outside every
  // landmark is unreachable by landmark navigation.
  /*
   * The build notice.
   *
   * Present in both presentation modes, because both statements it makes are
   * load-bearing: no bank is contacted, and every value is synthetic. Demo
   * mode shortens it to one line rather than removing it — a partner
   * demonstration that let a fake authorisation look real is the one outcome
   * this product cannot ship.
   */
  const banner = el("aside", {
    class: "nura-banner",
    attrs: { "aria-label": "About this build" },
  });

  function syncBanner(): void {
    banner.replaceChildren(
      el("span", { class: "nura-banner__icon", staticHtml: icon("shield-check", 16) }),
      el("p", {
        class: "nura-banner__text",
        text: state.isDemo
          ? "Demonstration — no bank is contacted and no transfer is authorised."
          : "Interface demonstration. Amounts are entered for real, but no bank is contacted, " +
            "no transfer is authorised and every value shown is synthetic.",
      }),
    );
    banner.dataset["mode"] = state.presentation;
  }

  syncBanner();
  state.subscribe(syncBanner);

  const footer = el("footer", { class: "nura-footer" }, [
    el("div", { class: "nura-footer__inner" }, [
      attributionLine(state),
      el("nav", { class: "nura-footer__links", attrs: { "aria-label": "Secondary" } }, [
        el("a", { text: "Transfer", attrs: { href: "/" } }),
        el("a", { text: "Receiver reference", attrs: { href: "/receiver-reference" } }),
        el("a", { text: "Design foundations", attrs: { href: "/foundations" } }),
      ]),
    ]),
  ]);

  const root = el("div", { class: "nura-shell" }, [
    el("a", {
      class: "nura-skip-link",
      text: "Skip to main content",
      attrs: { href: "#nura-main" },
    }),
    appBar,
    banner,
    outlet,
    footer,
  ]);

  return { root, outlet };
}
